import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, ClipboardList, Users, PartyPopper } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import EventbriteRegistrationFlow, { RegistrationFlowResult } from "@/components/EventbriteRegistrationFlow";
import TeamSelectionStep from "./TeamSelectionStep";
import ConfirmationStep from "./ConfirmationStep";
import InviteTeamMemberDialog from "./InviteTeamMemberDialog";

interface ChallengeJoinFlowProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  challengeId: string;
  eventId: string | null;
  challengeTitle: string;
  initialAction?: "join" | "create";
  startAtStep?: number;
  preRegistrationId?: string;
}

const FLOW_STEPS = [
  { 
    number: 1, 
    title: "Register", 
    description: "Complete event registration",
    icon: ClipboardList,
    whyNeeded: "We need your details to prepare for your attendance and connect you with your team."
  },
  { 
    number: 2, 
    title: "Choose Team", 
    description: "Join or create a team",
    icon: Users,
    whyNeeded: "Teams collaborate to build solutions during the event. Join an existing team or start your own."
  },
  { 
    number: 3, 
    title: "Confirmation", 
    description: "You're all set!",
    icon: PartyPopper,
    whyNeeded: "Review your registration and team status."
  },
];

export type FlowResult = {
  action: "created_team" | "requested_join" | "already_on_team";
  teamId: string;
  teamName: string;
};

const ChallengeJoinFlow: React.FC<ChallengeJoinFlowProps> = ({
  open,
  onOpenChange,
  challengeId,
  eventId,
  challengeTitle,
  initialAction = "join",
  startAtStep,
  preRegistrationId,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState(startAtStep || 1);
  const [registrationId, setRegistrationId] = useState<string | null>(preRegistrationId || null);
  const [flowResult, setFlowResult] = useState<FlowResult | null>(null);
  const [checkingRegistration, setCheckingRegistration] = useState(!startAtStep);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  
  // Event info for registration CTA
  const [eventInfo, setEventInfo] = useState<{
    id: string;
    name: string;
    slug: string;
  } | null>(null);
  const [isRegisteredForEvent, setIsRegisteredForEvent] = useState(true);

  // Check for existing registration and event info on mount
  useEffect(() => {
    if (open) {
      checkExistingRegistration();
      fetchEventInfo();
    }
  }, [open]);

  const checkExistingRegistration = async () => {
    // Skip check if startAtStep is provided (user is already authenticated and registered)
    if (startAtStep) {
      setCheckingRegistration(false);
      return;
    }
    
    setCheckingRegistration(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setCheckingRegistration(false);
        return;
      }

      setUserEmail(user.email || null);

      // Check if user already has a registration
      const { data: registration } = await supabase
        .from("event_registrations")
        .select("id")
        .eq("email", user.email)
        .maybeSingle();

      if (registration) {
        // Skip to step 2
        setRegistrationId(registration.id);
        setCurrentStep(2);
      }
    } catch (error) {
      console.error("Error checking registration:", error);
    } finally {
      setCheckingRegistration(false);
    }
  };

  const fetchEventInfo = async () => {
    if (!eventId) {
      setEventInfo(null);
      return;
    }

    try {
      // Fetch event details
      const { data: event } = await supabase
        .from("events")
        .select("id, name, slug")
        .eq("id", eventId)
        .single();

      if (event) {
        setEventInfo({
          id: event.id,
          name: event.name,
          slug: event.slug,
        });
      }

      // Check if user is registered for the event
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: registration } = await supabase
          .from("event_registrations")
          .select("id")
          .eq("user_id", user.id)
          .eq("event_id", eventId)
          .maybeSingle();

        setIsRegisteredForEvent(!!registration);
      }
    } catch (error) {
      console.error("Error fetching event info:", error);
    }
  };

  const handleRegistrationComplete = (regId: string) => {
    setRegistrationId(regId);
    setCurrentStep(2);
  };

  const handleRegistrationFlowComplete = (result: RegistrationFlowResult) => {
    setRegistrationId(result.registrationId);
    if (result.isTeamParticipant) {
      setCurrentStep(2);
    } else {
      // Non-participant - close the dialog and show confirmation
      onOpenChange(false);
      toast.success("Registration confirmed! You're all set for the event.");
    }
  };

  const handleTeamSelected = (result: FlowResult) => {
    setFlowResult(result);
    setCurrentStep(3);
  };

  const handleFlowComplete = () => {
    // Invalidate dashboard caches to ensure fresh data is fetched
    queryClient.invalidateQueries({ queryKey: ["dashboard-hero-state"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard-quick-stats"] });
    queryClient.invalidateQueries({ queryKey: ["user-challenges"] });
    
    onOpenChange(false);
    
    // Redirect to dashboard with success state
    navigate("/dashboard", {
      state: {
        challengeAction: flowResult?.action,
        teamName: flowResult?.teamName,
        teamId: flowResult?.teamId,
        challengeTitle: challengeTitle,
      },
    });

    toast.success(
      flowResult?.action === "created_team"
        ? "Team created! Redirecting to your dashboard..."
        : "Request sent! Redirecting to your dashboard..."
    );
  };

  const handleOpenInviteDialog = () => {
    setShowInviteDialog(true);
  };

  const handleInviteSent = () => {
    // Keep dialog open for more invites - user can close when done
  };

  const currentStepInfo = FLOW_STEPS[currentStep - 1];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Join {challengeTitle}</DialogTitle>
        </DialogHeader>

        {/* Progress Bar */}
        <div className="py-4">
          <div className="flex items-center justify-center">
            {FLOW_STEPS.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300",
                      currentStep > step.number
                        ? "bg-primary text-primary-foreground"
                        : currentStep === step.number
                        ? "bg-primary text-primary-foreground ring-4 ring-primary/20"
                        : "bg-muted text-muted-foreground"
                    )}
                  >
                    {currentStep > step.number ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <step.icon className="w-5 h-5" />
                    )}
                  </div>
                  <span
                    className={cn(
                      "text-xs mt-2 font-medium transition-colors hidden sm:block",
                      currentStep >= step.number ? "text-foreground" : "text-muted-foreground"
                    )}
                  >
                    {step.title}
                  </span>
                </div>
                {index < FLOW_STEPS.length - 1 && (
                  <div
                    className={cn(
                      "w-12 sm:w-20 h-0.5 mx-2 transition-colors duration-300",
                      currentStep > step.number ? "bg-primary" : "bg-muted"
                    )}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Why this step */}
          <div className="mt-4 p-3 bg-muted/50 rounded-lg text-center">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Why this step? </span>
              {currentStepInfo.whyNeeded}
            </p>
          </div>
        </div>

        {/* Step Content */}
        <div className="mt-2">
          {checkingRegistration ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : (
            <>
              {currentStep === 1 && (
                <EventbriteRegistrationFlow
                  onComplete={handleRegistrationFlowComplete}
                  onCancel={() => onOpenChange(false)}
                  showEventbriteFirst={false}
                  embedded={true}
                />
              )}

              {currentStep === 2 && (
                <TeamSelectionStep
                  challengeId={challengeId}
                  onTeamSelected={handleTeamSelected}
                  initialAction={initialAction}
                />
              )}

              {currentStep === 3 && flowResult && (
                <>
                  <ConfirmationStep
                    result={flowResult}
                    challengeTitle={challengeTitle}
                    onComplete={handleFlowComplete}
                    onBrowseOtherTeams={() => setCurrentStep(2)}
                    onInviteTeammates={
                      flowResult.action === "created_team" || flowResult.action === "already_on_team"
                        ? handleOpenInviteDialog
                        : undefined
                    }
                    eventId={eventInfo?.id}
                    eventName={eventInfo?.name}
                    eventSlug={eventInfo?.slug}
                    isRegisteredForEvent={isRegisteredForEvent}
                  />
                  
                  {/* Nested invite dialog for team leaders/members */}
                  <InviteTeamMemberDialog
                    open={showInviteDialog}
                    onOpenChange={setShowInviteDialog}
                    teamId={flowResult.teamId}
                    teamName={flowResult.teamName}
                    challengeId={challengeId}
                    isTeamLead={flowResult.action === "created_team"}
                    onInviteSent={handleInviteSent}
                  />
                </>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChallengeJoinFlow;
